import {
  ArrowForward,
  GppGoodOutlined,
  InfoOutlined,
  MenuBook,
  PeopleAltOutlined,
} from '@mui/icons-material';
import { Alert, Button, Link, Paper, Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import * as React from 'react';
import ReactDOM from 'react-dom/client';

import theme from './welcome-theme';

// Rendered into the page by WelcomeView, which also serves as LOGIN_URL.
// `next` is set only when the user was redirected here from a page they could not
// reach while signed out; it doubles as the signal that this visit is a bounce
// rather than someone arriving at the front door.
const csrfToken = document.body.dataset.csrf;
const nextUrl = document.body.dataset.next ?? '';
const authError = document.body.dataset.authError ?? '';

interface LoginFormProps {
  label: string;
  // Submit as soon as the form is on the page, rather than waiting for a click.
  autoSubmit?: boolean;
}

/**
 * The sign-in form. social-auth-app-django 6 requires POST at the OAuth begin
 * endpoint, so this is the only way into the application.
 */
const LoginForm: React.FC<LoginFormProps> = ({ label, autoSubmit = false }) => {
  const formRef = React.useRef<HTMLFormElement>(null);
  const submitted = React.useRef(false);

  React.useEffect(() => {
    // The ref guard keeps StrictMode's double-invoked effect from submitting twice.
    if (!autoSubmit || submitted.current) {
      return;
    }
    submitted.current = true;

    // Drop `next` from this history entry before leaving. Without this, pressing
    // back after signing in returns to /?next=... and this effect fires again,
    // throwing the user forward to where they just came from.
    window.history.replaceState(null, '', '/');
    formRef.current?.submit();
  }, [autoSubmit]);

  return (
    <form ref={formRef} method="post" action="/accounts/login/google-oauth2/">
      <input type="hidden" name="csrfmiddlewaretoken" value={csrfToken} />
      {nextUrl && <input type="hidden" name="next" value={nextUrl} />}
      <Button
        variant="contained"
        color="secondary"
        type="submit"
        endIcon={<ArrowForward />}
        sx={{ width: { xs: '100%', sm: 'fit-content' }, color: 'black' }}
      >
        {label}
      </Button>
    </form>
  );
};

/** Branded page shell shared by the welcome page and the signing-in interstitial. */
const PageFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      bgcolor: 'primary.dark',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Box sx={{ p: 3, pb: { xs: 1, sm: 1 } }} component="main">
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <img
          src="/static/images/logo.png"
          alt="University of Cambridge Logo"
          style={{ maxWidth: '200px' }}
        />
        <Stack>
          <Typography variant="subtitle2">Research Services</Typography>
          <Typography variant="caption">Enabling Research Excellence</Typography>
        </Stack>
      </Stack>
    </Box>
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 4 },
        pt: { xs: 1, sm: 0 },
        pb: { xs: 4, sm: 0 },
      }}
    >
      <Stack spacing={3} sx={{ maxWidth: '680px', width: '100%' }}>
        {children}
      </Stack>
    </Box>
  </Box>
);

/**
 * Shown while the sign-in POST is on its way. Usually a flash on the way to Google,
 * but it is also what sits behind Google's own account chooser, so it says what is
 * happening. The button is there in case the automatic submit does not take.
 */
const SigningIn: React.FC = () => (
  <PageFrame>
    <Typography
      variant="h1"
      sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '3rem' } }}
    >
      Signing you in&hellip;
    </Typography>
    <Typography variant="body1">
      Taking you to the University sign-in page, then back to the page you asked for. An
      expired session is the usual reason for ending up here.
    </Typography>
    <LoginForm label="Continue to sign in" autoSubmit />
  </PageFrame>
);

/** The public landing page. */
const Welcome: React.FC = () => (
  <PageFrame>
    <Typography
      variant="h1"
      sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '3rem' } }}
    >
      Research Dashboard
    </Typography>
    {authError && (
      <Alert severity="error" variant="filled">
        {authError} Please check that you are signing in with your University of Cambridge
        (@cam.ac.uk) Google account.
      </Alert>
    )}
    <Typography variant="body1">
      View grants, budgets and project data in one place, using data from CUFS, refreshed
      overnight.
    </Typography>
    <Typography
      variant="overline"
      component="h2"
      sx={{ fontSize: '1rem', fontWeight: 700 }}
    >
      Who is this for?
    </Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 4 }}>
      <Stack spacing={1} direction="row">
        <MenuBook fontSize="large" />
        <Typography variant="subtitle1" component="h4">
          Principal Investigators
        </Typography>
      </Stack>
      <Stack spacing={1} direction="row">
        <PeopleAltOutlined fontSize="large" />
        <Typography variant="subtitle1" component="h4">
          Co-Investigators
        </Typography>
      </Stack>
      <Stack spacing={1} direction="row">
        <GppGoodOutlined fontSize="large" />
        <Typography variant="subtitle1" component="h4">
          Budget &amp; Reporting Staff
        </Typography>
      </Stack>
    </Stack>
    <LoginForm label="Log in to Research Dashboard" />
    <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 2 }}>
      <Stack direction="row" spacing={2}>
        <InfoOutlined fontSize="large" />
        <Stack spacing={2}>
          <Typography
            variant="h2"
            sx={{ fontWeight: 500, fontSize: '1.25rem', lineHeight: 1.6 }}
          >
            How do I see data?
          </Typography>

          <Typography variant="body2">
            You can view award-level and project-level (budget) data if you are:
            <ul>
              <li>
                Assigned a <strong>role</strong> on awards or projects within{' '}
                <strong>CUFS</strong> (the University Finance System), like PIs, or
              </li>
              <li>
                Set up as a <strong>Department Grants Administrator in CUFS</strong>
              </li>
            </ul>
            If you cannot see data after logging in, contact your Department Grants
            Administrator. Getting access can take 5 to 10 working days.
          </Typography>
        </Stack>
      </Stack>
    </Paper>
    <Typography variant="body2">
      Research Dashboard data must only be used for legitimate University purposes
      connected with project and award management, including budgeting, forecasting and
      funder requirements. Do not share Dashboard data with anyone who is not authorised
      to access it. If you access or disclose information in error, report it immediately
      to the{' '}
      <Link
        href={
          'mailto:CRO_ServiceDesk@admin.cam.ac.uk' +
          '?subject=Research Dashboard Data Security'
        }
        color="inherit"
        target="_blank"
      >
        CRO Service Desk
      </Link>
      .
    </Typography>
    <Typography variant="body2">
      <Link
        href="https://www.information-compliance.admin.cam.ac.uk/data-protection#heading2"
        color="inherit"
        target="_blank"
      >
        Refer to the data protection terms
      </Link>
    </Typography>
  </PageFrame>
);

const container = document.getElementById('welcome-root');
const root = ReactDOM.createRoot(container!);
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {nextUrl ? <SigningIn /> : <Welcome />}
    </ThemeProvider>
  </React.StrictMode>
);
